<template>
	<div class="overlay-layer-toolbar">
		<b-button variant="link" size="sm" title="Properties" @click="$emit('properties', $event)"><b-icon icon="gear-fill" /></b-button>
		<b-button variant="link" size="sm" title="Position and size" @click="$emit('position', $event)"><b-icon icon="bounding-box" /></b-button>
		<b-button variant="link" size="sm" :title="warping ? 'Done distorting' : 'Distort (perspective)'" :class="{ 'olt-active': warping }" @click="$emit('distort')">
			<!-- A grid receding to a vanishing point: a flat quad seen in perspective. -->
			<svg class="olt-perspective" viewBox="0 0 16 16" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">
				<path d="M1.5 13.5 L14.5 13.5 L11 2.5 L5 2.5 Z" />
				<path d="M8 2.5 V13.5 M3.2 8.2 H12.8 M2.4 10.8 H13.6" />
			</svg>
		</b-button>
		<b-button v-if="mediaLayer" variant="link" size="sm" :title="cropping ? 'Done cropping (drag a handle, or hold Alt any time)' : 'Crop mode (drag a handle, or hold Alt any time)'" :class="{ 'olt-active': cropping }" @click="$emit('crop')"><b-icon icon="crop" /></b-button>
		<b-button v-if="keepsRatio" variant="link" size="sm" :title="stretching ? 'Done stretching (resize any handle, or hold Shift any time)' : 'Stretch mode (resize any handle, or hold Shift any time)'" :class="{ 'olt-active': stretching }" @click="$emit('stretch')"><b-icon icon="arrows-angle-expand" /></b-button>
		<b-button v-if="canReset" variant="link" size="sm" title="Reset transform (rotation, distortion, crop and media size)" @click="$emit('reset-transform')"><b-icon icon="bootstrap-reboot" /></b-button>
		<template v-if="isGrid">
			<span class="olt-divider"></span>
			<b-button v-for="f in fits" :key="f.value" variant="link" size="sm" :title="f.title" :class="{ 'olt-active': gridSetting('fit', 'fill') === f.value }" @click="$emit('setting', 'fit', f.value)">{{f.label}}</b-button>
			<span class="olt-divider"></span>
			<b-button v-for="a in verticalAligns" :key="`v-${a.value}`" variant="link" size="sm" :title="a.title" :class="{ 'olt-active': gridSetting('vAlign', 'center') === a.value }" @click="$emit('setting', 'vAlign', a.value)"><b-icon :icon="a.icon" /></b-button>
			<span class="olt-divider"></span>
			<b-button v-for="a in horizontalAligns" :key="`h-${a.value}`" variant="link" size="sm" :title="a.title" :class="{ 'olt-active': gridSetting('hAlign', 'center') === a.value }" @click="$emit('setting', 'hAlign', a.value)"><b-icon :icon="a.icon" /></b-button>
		</template>
		<span class="olt-divider"></span>
		<b-button variant="link" size="sm" title="Move up" @click="$emit('move-up')"><b-icon icon="arrow-up" /></b-button>
		<b-button variant="link" size="sm" title="Move down" @click="$emit('move-down')"><b-icon icon="arrow-down" /></b-button>
		<b-button variant="link" size="sm" title="Duplicate" @click="$emit('duplicate')"><b-icon icon="files" /></b-button>
		<b-button variant="link" size="sm" title="Delete" class="olt-danger" @click="$emit('delete')"><b-icon icon="trash" /></b-button>
	</div>
</template>

<script>
import { isMediaLayer, keepsAspectRatio } from './layerTypes';

export default {
	props: {
		layer: { type: Object, required: true },
		warping: { type: Boolean, default: false },
		cropping: { type: Boolean, default: false },
		stretching: { type: Boolean, default: false },
		canReset: { type: Boolean, default: false },
	},
	data() { return {
		fits: [
			{ value: 'fit', label: 'Fit', title: 'Fit: keep every picture whole and place the group inside the layer' },
			{ value: 'fill', label: 'Fill', title: 'Fill: cover the whole layer, cropping each picture to its cell' },
		],
		verticalAligns: [
			{ value: 'top', icon: 'align-top', title: 'Align top' },
			{ value: 'center', icon: 'align-middle', title: 'Align vertically centered' },
			{ value: 'bottom', icon: 'align-bottom', title: 'Align bottom' },
		],
		horizontalAligns: [
			{ value: 'left', icon: 'text-left', title: 'Align left' },
			{ value: 'center', icon: 'text-center', title: 'Align horizontally centered' },
			{ value: 'right', icon: 'text-right', title: 'Align right' },
		],
	}},
	methods: {
		gridSetting(key, fallback) { return this.layer.props?.settings?.[key] || fallback; },
	},
	computed: {
		isGrid() { return this.layer.type === 'widget' && this.layer.props?.widgetType === 'camera-grid'; },
		mediaLayer() { return isMediaLayer(this.layer); },
		keepsRatio() { return keepsAspectRatio(this.layer); },
	},
};
</script>

<style lang="scss" scoped>
@import '../../plugins/variable.scss';

.overlay-layer-toolbar {
	display: flex;
	align-items: center;
	padding: .15rem .3rem;
	background: $gray-400;
	border: 1px solid $border-color;
	border-radius: .4rem;
	box-shadow: 0 4px 14px rgba(0, 0, 0, .5);
	white-space: nowrap;

	.btn { color: #fff; padding: .15rem .4rem; }
	.btn:hover { background: $gray-600; }
	.olt-danger { color: #ff6b8a; }
	.olt-active { background: $green; }
}

.olt-perspective {
	display: block;
	width: 1.1em;
	height: 1.1em;
}

.olt-divider {
	width: 1px;
	height: 18px;
	margin: 0 .2rem;
	background: $border-color;
}
</style>
